import React, { useEffect, useRef, useState } from 'react';
import { styles } from '../../../utils/styles';
import { YourBuildWidgetConfig } from '../../Canvas/Widgets/YourBuildWidget/YourBuildWidget';
import { useComponentRegistry } from '../../../contexts/ComponentRegistryContext';

interface YourBuildWidgetConfigPanelProps {
  id?: string;
  config: YourBuildWidgetConfig;
  onConfigChange: (config: YourBuildWidgetConfig) => void;
}

const YourBuildWidgetConfigPanel: React.FC<YourBuildWidgetConfigPanelProps> = ({
  config,
  onConfigChange
}) => {
  const { saveWidgetConfig } = useComponentRegistry();
  const descriptionRef = useRef<HTMLDivElement>(null);

  // Debouncing infrastructure for text inputs
  const titleDebounceRef = useRef<NodeJS.Timeout>();
  const shareButtonLabelDebounceRef = useRef<NodeJS.Timeout>();
  const addToCartButtonLabelDebounceRef = useRef<NodeJS.Timeout>();

  // Local state for immediate UI feedback
  const [localTitle, setLocalTitle] = useState<string>(config.title);
  const [localShareButtonLabel, setLocalShareButtonLabel] = useState<string>(config.shareButtonConfig.buttonLabel);
  const [localAddToCartButtonLabel, setLocalAddToCartButtonLabel] = useState<string>(config.addToCartButtonConfig.buttonLabel);

  // Sync local state when props change externally
  useEffect(() => {
    setLocalTitle(config.title);
  }, [config.title]);

  useEffect(() => {
    setLocalShareButtonLabel(config.shareButtonConfig.buttonLabel);
  }, [config.shareButtonConfig.buttonLabel]);

  useEffect(() => {
    setLocalAddToCartButtonLabel(config.addToCartButtonConfig.buttonLabel);
  }, [config.addToCartButtonConfig.buttonLabel]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (titleDebounceRef.current) {
        clearTimeout(titleDebounceRef.current);
      }
      if (shareButtonLabelDebounceRef.current) {
        clearTimeout(shareButtonLabelDebounceRef.current);
      }
      if (addToCartButtonLabelDebounceRef.current) {
        clearTimeout(addToCartButtonLabelDebounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (config) {
      saveWidgetConfig('your-build-widget', config);
    }
  }, [config, saveWidgetConfig]);
  
  useEffect(() => {
    if (descriptionRef.current && descriptionRef.current.innerHTML !== config.description) {
      descriptionRef.current.innerHTML = config.description;
    }
  }, [config.description]);
  
  const handleChange = (key: keyof YourBuildWidgetConfig, value: any) => {
    onConfigChange({
      ...config,
      [key]: value
    });
  };

  const handleShareButtonChange = (key: keyof typeof config.shareButtonConfig, value: any) => {
    onConfigChange({
      ...config,
      shareButtonConfig: {
        ...config.shareButtonConfig,
        [key]: value
      }
    });
  };

  const handleAddToCartButtonChange = (key: keyof typeof config.addToCartButtonConfig, value: any) => {
    onConfigChange({
      ...config,
      addToCartButtonConfig: {
        ...config.addToCartButtonConfig,
        [key]: value
      }
    });
  };

  // Debounced handlers for text inputs
  const handleDebouncedTitleChange = (value: string) => {
    setLocalTitle(value);
    if (titleDebounceRef.current) {
      clearTimeout(titleDebounceRef.current);
    }
    titleDebounceRef.current = setTimeout(() => {
      handleChange('title', value);
    }, 500);
  };

  const handleDebouncedShareButtonLabelChange = (value: string) => {
    setLocalShareButtonLabel(value);
    if (shareButtonLabelDebounceRef.current) {
      clearTimeout(shareButtonLabelDebounceRef.current);
    }
    shareButtonLabelDebounceRef.current = setTimeout(() => {
      handleShareButtonChange('buttonLabel', value);
    }, 500);
  };

  const handleDebouncedAddToCartButtonLabelChange = (value: string) => {
    setLocalAddToCartButtonLabel(value);
    if (addToCartButtonLabelDebounceRef.current) {
      clearTimeout(addToCartButtonLabelDebounceRef.current);
    }
    addToCartButtonLabelDebounceRef.current = setTimeout(() => {
      handleAddToCartButtonChange('buttonLabel', value);
    }, 500);
  };
  
  const handleDescriptionFormat = (command: string) => {
    if (descriptionRef.current) {
      descriptionRef.current.focus();
      
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        
        document.execCommand(command, false, '');
        
        descriptionRef.current.focus();
        
        try {
          selection.removeAllRanges();
          selection.addRange(range);
        } catch (e) {
          console.error('Range restoration failed, keeping focus', e);
        }
      } else {
        document.execCommand(command, false, '');
        descriptionRef.current.focus();
      }
    }
  };
  
  const handleDescriptionChange = (e: React.FormEvent<HTMLDivElement>) => {
    const htmlContent = e.currentTarget.innerHTML;
    handleChange('description', htmlContent);
  };

  return (
    <div className="tw-space-y-6 ">
      <div className='tw-p-3'>
        {/* Widget Header */}
      <div className="tw-flex tw-items-center tw-justify-between tw-pb-3 tw-border-b tw-border-gray-200">
        <h3 className="tw-text-base tw-font-semibold tw-text-[#172B4D]">Widget</h3>
        <button className="tw-text-gray-400 hover:tw-text-gray-600">
          <svg className="tw-w-5 tw-h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
          </svg>
        </button>
      </div>

      {/* Title Configuration */}
      <div className='tw-mt-3'>
        <label className="tw-flex tw-items-center tw-mb-3">
          <input
            type="checkbox"
            checked={config.showTitle}
            onChange={(e) => handleChange('showTitle', e.target.checked)}
            className={styles.checkboxInput}
          />
          <span className={styles.checkboxLabel}>Show 'Title'</span>
        </label>
        
        {config.showTitle && (
          <div className="tw-ml-6">
            <input
              type="text"
              value={localTitle}
              onChange={(e) => handleDebouncedTitleChange(e.target.value)}
              className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2"
              placeholder="Enter title"
            />
          </div>
        )}
      </div>

      {/* Share Button Configuration */}
      <div>
        <label className="tw-flex tw-items-center tw-mb-3">
          <input
            type="checkbox"
            checked={config.showShareButton}
            onChange={(e) => handleChange('showShareButton', e.target.checked)}
            className={styles.checkboxInput}
          />
          <span className={styles.checkboxLabel}>Show 'Share' Button</span>
        </label>
        
        {config.showShareButton && (
          <div className="tw-ml-6 tw-space-y-3">
            <label className="tw-flex tw-items-center">
              <input
                type="checkbox"
                checked={config.shareButtonConfig.showText}
                onChange={(e) => handleShareButtonChange('showText', e.target.checked)}
                className={styles.checkboxInput}
              />
              <span className={styles.checkboxLabel}>Show Text</span>
            </label>
            
            {config.shareButtonConfig.showText && (
              <div className="tw-ml-6">
                <label className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                  Button Label
                </label>
                <input
                  type="text"
                  value={localShareButtonLabel}
                  onChange={(e) => handleDebouncedShareButtonLabelChange(e.target.value)}
                  className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2"
                  placeholder="Enter button label"
                />
              </div>
            )}
            
            <label className="tw-flex tw-items-center">
              <input
                type="checkbox"
                checked={config.shareButtonConfig.showIcon}
                onChange={(e) => handleShareButtonChange('showIcon', e.target.checked)}
                className={styles.checkboxInput}
              />
              <span className={styles.checkboxLabel}>Show Icon</span>
            </label>
          </div>
        )}
      </div>

      {/* Description Configuration */}
      <div>
        <label className="tw-flex tw-items-center tw-mb-3">
          <input
            type="checkbox"
            checked={config.showDescription}
            onChange={(e) => handleChange('showDescription', e.target.checked)}
            className={styles.checkboxInput}
          />
          <span className={styles.checkboxLabel}>Show Description</span>
        </label>
        
        {config.showDescription && (
          <div className="tw-ml-6">
            {/* Bold and Italic buttons */}
            <div className="tw-flex tw-gap-1 tw-mb-2">
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDescriptionFormat('bold');
                }}
                className="tw-w-8 tw-h-8 tw-border tw-border-[#D1D1D6] tw-rounded tw-flex tw-items-center tw-justify-center tw-bg-white hover:tw-bg-gray-50 active:tw-bg-gray-100"
              >
                <span className="tw-font-extrabold tw-text-sm">B</span>
              </button>
              <button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDescriptionFormat('italic');
                }}
                className="tw-w-8 tw-h-8 tw-border tw-border-[#D1D1D6] tw-rounded tw-flex tw-items-center tw-justify-center tw-bg-white hover:tw-bg-gray-50 active:tw-bg-gray-100"
              >
                <span className="tw-italic tw-text-sm tw-font-extrabold">I</span>
              </button>
            </div>
            
            {/* Rich text editor */}
            <div
              ref={descriptionRef}
              contentEditable
              suppressContentEditableWarning={true}
              onInput={handleDescriptionChange}
              onBlur={handleDescriptionChange}
              className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2 tw-min-h-[100px] tw-p-3"
              style={{ wordWrap: 'break-word' }}
            />
          </div>
        )}
      </div>

      {/* Add to Cart Button Configuration */}
      <div>
        <label className="tw-flex tw-items-center tw-mb-3">
          <input
            type="checkbox"
            checked={config.showAddToCartButton}
            onChange={(e) => handleChange('showAddToCartButton', e.target.checked)}
            className={styles.checkboxInput}
          />
          <span className={styles.checkboxLabel}>Show 'Add to Cart' Button</span>
        </label>
        
        {config.showAddToCartButton && (
          <div className="tw-ml-6 tw-space-y-3">
            <label className="tw-flex tw-items-center">
              <input
                type="checkbox"
                checked={config.addToCartButtonConfig.showText}
                onChange={(e) => handleAddToCartButtonChange('showText', e.target.checked)}
                className={styles.checkboxInput}
              />
              <span className={styles.checkboxLabel}>Show Text</span>
            </label>
            
            {config.addToCartButtonConfig.showText && (
              <div className="tw-ml-6">
                <label className="tw-block tw-text-[#6D788C] tw-text-sm tw-mb-2">
                  Button Label
                </label>
                <input
                  type="text"
                  value={localAddToCartButtonLabel}
                  onChange={(e) => handleDebouncedAddToCartButtonLabelChange(e.target.value)}
                  className="tw-w-full tw-border tw-text-sm tw-text-[#172B4D] tw-font-medium tw-border-[#D1D1D6] tw-rounded tw-px-3 tw-py-2"
                  placeholder="Enter button label"
                />
              </div>
            )}
            
            <label className="tw-flex tw-items-center">
              <input
                type="checkbox"
                checked={config.addToCartButtonConfig.showIcon}
                onChange={(e) => handleAddToCartButtonChange('showIcon', e.target.checked)}
                className={styles.checkboxInput}
              />
              <span className={styles.checkboxLabel}>Show Icon</span>
            </label>
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default YourBuildWidgetConfigPanel;
